import { useState, useEffect, useRef } from 'react';
import { getIpfsImageUrl, IPFS_GATEWAYS } from '@/lib/ipfs-utils';

interface IpfsImageProps {
  src: string | null | undefined;
  alt: string;
  className?: string;
  fallback?: React.ReactNode;
  onError?: () => void;
  loading?: 'lazy' | 'eager';
  crossOrigin?: 'anonymous' | 'use-credentials';
  timeout?: number; // Таймаут в миллисекундах для переключения на следующий gateway
}

/**
 * Компонент для отображения изображений из IPFS с автоматическим fallback
 * При ошибке загрузки или таймауте автоматически пытается загрузить с других gateways
 */
export const IpfsImage = ({
  src,
  alt,
  className,
  fallback,
  onError,
  loading = 'lazy',
  crossOrigin = 'anonymous',
  timeout = 3000, // 3 секунды по умолчанию для быстрого переключения на другой gateway
}: IpfsImageProps) => {
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    // Очистка предыдущего таймаута
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Сброс состояния при изменении src
    setCurrentAttempt(0);
    setHasError(false);
    
    const url = getIpfsImageUrl(src, 0);
    setImageUrl(url);

    // Устанавливаем таймаут для быстрого переключения на следующий gateway
    if (url && src) {
      // Для первой попытки используем более короткий таймаут (2 секунды) для быстрого обнаружения DNS ошибок
      const firstTimeout = 2000;
      timeoutRef.current = setTimeout(() => {
        // Если изображение еще не загрузилось, пробуем следующий gateway
        if (imageRef.current && !imageRef.current.complete) {
          console.log(`IPFS: таймаут загрузки с gateway #0 после ${firstTimeout}ms, пробуем следующий для ${src}`);
          tryNextGateway();
        }
      }, firstTimeout);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, timeout]);

  const tryNextGateway = () => {
    setCurrentAttempt((prevAttempt) => {
      const nextAttempt = prevAttempt + 1;
      
      if (nextAttempt < IPFS_GATEWAYS.length) {
        console.log(`IPFS: попытка загрузки с gateway #${nextAttempt} для ${src}`);
        const url = getIpfsImageUrl(src, nextAttempt);
        setImageUrl(url);
        
        // Устанавливаем новый таймаут для следующей попытки
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          if (imageRef.current && !imageRef.current.complete) {
            tryNextGateway();
          }
        }, timeout);
        
        return nextAttempt;
      } else {
        // Все gateways испробованы
        console.error(`IPFS: не удалось загрузить изображение после ${IPFS_GATEWAYS.length} попыток: ${src}`);
        setHasError(true);
        onError?.();
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        return prevAttempt;
      }
    });
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // Очищаем таймаут, так как получили ошибку
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Логируем тип ошибки для отладки
    const target = e.currentTarget;
    console.warn(`IPFS: ошибка загрузки с gateway #${currentAttempt} для ${src}`, {
      url: target.src,
      naturalWidth: target.naturalWidth,
      naturalHeight: target.naturalHeight,
    });
    
    tryNextGateway();
  };

  const handleLoad = () => {
    // Изображение успешно загрузилось, очищаем таймаут
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  if (!imageUrl || hasError) {
    return <>{fallback}</>;
  }

  return (
    <img
      ref={imageRef}
      src={imageUrl}
      alt={alt}
      className={className}
      onError={handleError}
      onLoad={handleLoad}
      loading={loading}
      crossOrigin={crossOrigin}
    />
  );
};

// Default export для совместимости с lazy loading
export default IpfsImage;
